import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { ISubscription, getBrand, getHostName, noop } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  useGetAllSubscriptionsByUserQuery,
  useGetSubscriptionsByCompanyQuery,
} from '@blockpulse3/graphql/hooks';
import { BrandIcon } from '@blockpulse3/ui/brand-theme';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { InvitationsTable } from './InvitationsTable';
import { concatUserSubscriptions } from './utils';

type Props = {
  onInvitationClick?: (subscription: ISubscription, subCompanyId?: string) => void;
};

/**
 * InvitationList.
 * Dashboard section of subscription invitation tables (STARTED, PASSED).
 *
 * @returns {JSX.Element}
 */
export function InvitationList({ onInvitationClick = noop }: Props): JSX.Element {
  const i18nInvitations = useTranslations('Invitations.MainPage');
  const i18nInvitationsTableEmpty = useTranslations('Invitations.TableEmpty');

  const { user } = useUser();
  const { companyId = '' } = useParams();

  const {
    data: userReq,
    loading: subscriptionsUserLoading,
    error: subscriptionsUserError,
  } = useGetAllSubscriptionsByUserQuery({
    variables: { userId: user?.id || '' },
    fetchPolicy: 'cache-and-network',
    skip: !user?.id || !!companyId,
  });
  const {
    data: companyReq,
    loading: subscriptionsCompanyLoading,
    error: subscriptionsCompanyError,
  } = useGetSubscriptionsByCompanyQuery({
    variables: { companyId: companyId || '' },
    fetchPolicy: 'cache-and-network',
    skip: !companyId,
  });

  if ((!companyId && !user?.id) || subscriptionsCompanyLoading || subscriptionsUserLoading) {
    return (
      <Skeleton>
        <Card height="330px" />
      </Skeleton>
    );
  }

  if (subscriptionsUserError || subscriptionsCompanyError) {
    return <ErrorQueryCard h="330px" />;
  }

  const subscriptions = userReq?.getIndividualIdentityByUserId
    ? concatUserSubscriptions(userReq?.getIndividualIdentityByUserId)
    : companyReq?.getSubscriptionsByCompany;

  /* ** Sort subscriptions by end of subscription date ** */
  const sortSubscriptions = (a: ISubscription, b: ISubscription): number =>
    dayjs(a.fundraising.endSubscriptionDate).diff(dayjs(b.fundraising.endSubscriptionDate));

  /* ** Split elements of the getSubscriptions request according to subscriptions status ** */
  const subscriptionsCurrent = (subscriptions || [])
    .filter((subscription) =>
      [FundraisingStatus.STARTED, FundraisingStatus.CLOSED, FundraisingStatus.REVIEWED].includes(
        subscription.fundraising.status,
      ),
    )
    .sort(sortSubscriptions);
  const subscriptionsPassed = (subscriptions || [])
    .filter((subscription) => subscription.fundraising.status === FundraisingStatus.FINALIZED)
    .sort(sortSubscriptions);

  const handleInvitationClick = (subscription: ISubscription, subCompanyId?: string): void => {
    onInvitationClick(subscription, subCompanyId);
  };

  const hostname = getHostName();
  const brand = getBrand(hostname);

  const isAxerealSection =
    brand === 'axereal' &&
    dayjs().isBefore('2023-09-12') &&
    subscriptionsCurrent.length === 0 &&
    subscriptionsPassed.length === 0;

  return (
    <Card>
      <CardHeader>
        <Heading size="md">{i18nInvitations('MyInvitations')}</Heading>
      </CardHeader>
      <CardBody>
        {isAxerealSection ? (
          <Stack layerStyle="emptyState" spacing="0">
            <Stack h="65px">
              <BrandIcon bgTheme="light" h="full" w="156px" />
            </Stack>
            <Text>
              {i18nInvitationsTableEmpty.rich('AxerealNoInvitations-1', {
                important: (chunks) => <Text as="b">{chunks}</Text>,
              })}
            </Text>
            <Text>{i18nInvitationsTableEmpty('AxerealNoInvitations-2')}</Text>
          </Stack>
        ) : (
          <Tabs defaultIndex={0}>
            <TabList>
              <Tab>
                {i18nInvitations('InProgress')}
                {subscriptionsCurrent.length > 0 && (
                  <Badge ml="2">{subscriptionsCurrent.length}</Badge>
                )}
              </Tab>
              <Tab>
                {i18nInvitations('Past')}
                {subscriptionsPassed.length > 0 && (
                  <Badge ml="2">{subscriptionsPassed.length}</Badge>
                )}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <InvitationsTable
                  values={subscriptionsCurrent}
                  onInvitationClick={handleInvitationClick}
                />
              </TabPanel>
              <TabPanel>
                <InvitationsTable
                  values={subscriptionsPassed}
                  onInvitationClick={handleInvitationClick}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </CardBody>
    </Card>
  );
}

export type FundraisingListProps = Props;
